"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { DZTypes } from "@dealerzone/libs";
import { useState, useTransition } from "react";

interface InventoryFiltersProps {
  makes: DZTypes.Make[];
  locations: DZTypes.Location[];
  filters: DZTypes.DealerFilter[];
  currentParams: { [key: string]: string | string[] | undefined };
}

export default function InventoryFilters({ makes, locations, filters, currentParams }: InventoryFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const [searchValue, setSearchValue] = useState((currentParams.q as string) || "");

  const availableMakes = makes.filter((make) => Number(make.stock) > 0);

  const buildParams = () => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(currentParams)) {
      if (value && key !== "page") {
        params.set(key, typeof value === "string" ? value : value.join(","));
      }
    }
    return params;
  };

  const updateFilter = (key: string, value: string | null) => {
    const params = buildParams();
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page");
    const queryString = params.toString();
    startTransition(() => {
      router.push(queryString ? `/inventory?${queryString}` : "/inventory");
    });
  };

  const toggleFilter = (key: string, value: string) => {
    const params = buildParams();
    const current = params.get(key);
    const values = current ? current.split(",") : [];

    if (values.includes(value)) {
      const newValues = values.filter((v) => v !== value);
      if (newValues.length > 0) {
        params.set(key, newValues.join(","));
      } else {
        params.delete(key);
      }
    } else {
      values.push(value);
      params.set(key, values.join(","));
    }
    params.delete("page");
    const queryString = params.toString();
    startTransition(() => {
      router.push(queryString ? `/inventory?${queryString}` : "/inventory");
    });
  };

  const isChecked = (key: string, value: string) => {
    const current = currentParams[key];
    if (!current) {
      return false;
    }
    const values = typeof current === "string" ? current : current.join(",");
    return values.split(",").includes(value);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilter("q", searchValue || null);
  };

  const categoryOptions = [
    { value: "car", label: "Cars" },
    { value: "truck", label: "Trucks" },
    { value: "suv", label: "SUVs" },
    { value: "other", label: "Other" },
  ];

  return (
    <div className={`space-y-6 ${isPending ? "opacity-50" : ""}`}>
      <form onSubmit={handleSearch}>
        <label htmlFor="search" className="block text-sm font-medium text-zinc-700 mb-1">
          Search
        </label>
        <div className="relative">
          <input
            type="text"
            id="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search vehicles..."
            className="w-full border border-zinc-300 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-red-600"
          >
            <i className="bootstrap-search"></i>
          </button>
        </div>
      </form>

      <div>
        <label htmlFor="location" className="block text-sm font-medium text-zinc-700 mb-1">
          Location
        </label>
        <select
          id="location"
          value={(currentParams.location as string) || ""}
          onChange={(e) => updateFilter("location", e.target.value || null)}
          className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="">All Locations</option>
          <option value="california">California (On-Site)</option>
          <option value="utah">Utah (Offsite)</option>
          <option value="vegas">Las Vegas</option>
        </select>
      </div>

      {filters.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-zinc-700 mb-2">Title Status</h3>
          <div className="space-y-2">
            {filters.map((filter) => (
              <label key={filter.slug} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked("filters", filter.slug)}
                  onChange={() => toggleFilter("filters", filter.slug)}
                  className="w-4 h-4 text-red-600 border-zinc-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-zinc-700">{filter.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-medium text-zinc-700 mb-2">Body Type</h3>
        <div className="space-y-2">
          {categoryOptions.map((option) => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isChecked("category", option.value)}
                onChange={() => toggleFilter("category", option.value)}
                className="w-4 h-4 text-red-600 border-zinc-300 rounded focus:ring-red-500"
              />
              <span className="text-sm text-zinc-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {availableMakes.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-zinc-700 mb-2">Make</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto md:max-h-none md:overflow-visible">
            {availableMakes.map((make) => (
              <label key={make.slug} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked("make", make.slug)}
                  onChange={() => toggleFilter("make", make.slug)}
                  className="w-4 h-4 text-red-600 border-zinc-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-zinc-700">{make.name}</span>
                <span className="text-xs text-zinc-400">({Number(make.stock)})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {searchParams.toString() && (
        <a
          href="/inventory"
          className="block text-center text-sm text-red-600 hover:text-red-700 font-medium py-2 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
        >
          Clear All Filters
        </a>
      )}
    </div>
  );
}
